import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiClock, FiSearch, FiInbox, FiMapPin, FiUsers, FiDollarSign, FiGrid } from 'react-icons/fi';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { useAuth } from '../../contexts/AuthContext';
import { cartaServicosService, type AreaPublica, type AssuntoPublico } from '../../services/cartaServicos.service';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';

const FORMA_LABEL: Record<string, string> = {
  presencial: 'Presencial',
  online: 'Online',
  ambos: 'Presencial ou online',
};

export function CartaServicosPage() {
  const { session, cadastroCompleto } = useAuth();
  const [areas, setAreas] = useState<AreaPublica[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    cartaServicosService.listar(getEstabelecimentoAtualId())
      .then((d) => setAreas(d.filter((a) => a.assuntos.length > 0)))
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, []);

  const termo = busca.trim().toLowerCase();
  const areasFiltradas = !termo
    ? areas
    : areas
        .map((a) => ({
          ...a,
          assuntos: a.assuntos.filter((s) =>
            s.nome.toLowerCase().includes(termo) ||
            (s.descricao ?? '').toLowerCase().includes(termo),
          ),
        }))
        .filter((a) => a.assuntos.length > 0);

  return (
    <PortalPage
      titulo="Carta de serviços"
      subtitulo="Conheça e solicite os serviços disponíveis no portal."
      icone={<FiGrid size={26} />}
      largura="larga"
      voltarHref="/"
    >
      <div className="space-y-5">
        {!session && (
          <Alert variante="info">
            Você está navegando sem login. Para abrir uma solicitação,{' '}
            <Link to="/login" className="font-medium underline">entre</Link> ou{' '}
            <Link to="/cadastro" className="font-medium underline">cadastre-se</Link>.
            Para Ouvidoria você pode usar manifestação anônima sem login.
          </Alert>
        )}
        {session && !cadastroCompleto && (
          <Alert variante="aviso">
            Para abrir uma solicitação você precisa{' '}
            <Link to="/completar-cadastro" className="font-medium underline">completar seu cadastro</Link>.
          </Alert>
        )}

        <div className="max-w-md relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <Input
            type="search"
            placeholder="Buscar serviço por nome ou descrição..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-9"
          />
        </div>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : erro ? (
          <Alert variante="erro">{erro}</Alert>
        ) : areas.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">Nenhum serviço disponível no momento.</p>
              <p className="text-xs text-slate-500 mt-1">
                A prefeitura ainda não publicou serviços neste portal.
              </p>
            </div>
          </SectionCard>
        ) : areasFiltradas.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiSearch className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">Nenhum serviço encontrado para "{busca}".</p>
            </div>
          </SectionCard>
        ) : (
          <div className="space-y-8">
            {areasFiltradas.map((area) => (
              <section key={area.id ?? 'sem-area'}>
                <div className="flex items-baseline gap-2 mb-3">
                  <h2 className="text-lg font-semibold text-slate-900">{area.nome}</h2>
                  <span className="text-xs text-slate-500">{area.assuntos.length} serviço(s)</span>
                </div>
                {area.descricao && <p className="text-sm text-slate-500 mb-3">{area.descricao}</p>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {area.assuntos.map((s) => (
                    <CardServico key={s.id} servico={s} corArea={area.cor} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </PortalPage>
  );
}

function CardServico({ servico, corArea }: { servico: AssuntoPublico; corArea: string | null }) {
  const cor = corArea ?? '#313b64';
  // Sempre vai para detalhe — o usuário decide solicitar a partir de lá
  const linkDetalhe = `/servicos/detalhe/${servico.id}`;

  return (
    <Link to={linkDetalhe} className="block h-full">
      <div className="h-full rounded-2xl border border-slate-200 bg-white shadow-sm p-5 hover:shadow-md hover:border-institutional/40 transition cursor-pointer">
        <div className="flex items-start gap-3">
          <div
            className="flex-shrink-0 w-1 h-12 rounded"
            style={{ background: cor }}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-medium text-slate-900 line-clamp-2">{servico.nome}</h3>
              <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
            </div>
            {servico.descricao && (
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{servico.descricao}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
              {servico.prazo_dias && (
                <span className="flex items-center gap-1">
                  <FiClock size={11} /> {servico.prazo_dias}d úteis
                </span>
              )}
              {servico.forma_prestacao && (
                <span className="flex items-center gap-1">
                  <FiMapPin size={11} /> {FORMA_LABEL[servico.forma_prestacao] ?? servico.forma_prestacao}
                </span>
              )}
              {servico.publico_alvo && (
                <span className="flex items-center gap-1 truncate" title={servico.publico_alvo}>
                  <FiUsers size={11} /> {servico.publico_alvo}
                </span>
              )}
              {servico.tem_custo && (
                <span className="flex items-center gap-1 text-amber-700">
                  <FiDollarSign size={11} /> Com custo
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
